import { queryOptions, useQuery } from '@tanstack/react-query';
import { Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { ReadonlyObject } from '@/schema/schema';
import { httpChecked, queryClient } from '@/util/fetch';

const schema = ReadonlyObject({
  counts: Type.Record(Type.String(), Type.Integer()),
});

const checker = TypeCompiler.Compile(schema);

const queries = {
  customers: ['number-of-users-for-ref-customer'] as const,
  customer: (customer_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.customers,
        'customer',
        { customer_id },
        { language_code },
      ] as const,
      queryFn: ({ signal }) =>
        httpChecked.get(
          `/api/v2/number_of_users/${language_code}/customers/${customer_id}/projects`,
          checker,
          { signal },
        ),
    }),
};

export async function prefetchNumberOfUsers(
  customer_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.customer(customer_id, language_code),
    staleTime: Infinity,
  });
}

export async function invalidateNumberOfUsers() {
  await queryClient.invalidateQueries({ queryKey: queries.customers });
}

export function useNumberOfUsers(
  customer_id: string,
  project_id: string,
  language_code: string,
): number | undefined {
  const { data } = useQuery({
    ...queries.customer(customer_id, language_code),
    staleTime: 60_000,
    select: (data) => {
      return data.counts[project_id] ?? 0;
    },
  });

  return data;
}
